import { defineComponent, h } from "vue";
import { createRouter, createWebHistory, RouterView, useRoute } from "vue-router";

import RsBrand from "@/pages/Brand";
import RsAudit from "@/pages/Audit";
import RsError from "@/components/Error.vue";
import RsActivities from "@/pages/Activities.vue";
import RsCase from "@/pages/Case.vue";
import RsEvidenceFrame from "@/pages/EvidenceFrame.vue";
import RsAuth from "@/pages/Auth.vue";
import RsLogs from "@/pages/Logs.vue";

import SummarizerMoved from "@/pages/SummarizerMoved.vue";

import RsRoot from "@/pages/Root";
import RsBrandRoot from "@/pages/BrandRoot.vue";
import RsUpload from "@/pages/Upload";
import RsQuarantine from "@/pages/Quarantine";

import CaseAccess from "@/pages/CaseAccess";
import CaseSearch from "@/pages/CaseSearch";
import CaseHistory from "@/pages/CaseHistory";

import Jobs from "@/pages/Jobs";

import type { RouteParamsGeneric, RouterOptions } from "vue-router";

export type RouteTitles = string | ((params: RouteParamsGeneric, isPageTitle: boolean) => string);

const titleSuffix = (isPageTitle: boolean) => (isPageTitle ? " - Recordsure" : "");
const titles = {
  default: "EDX",
  CaseSearch: (_, isTitle) => `Case Search Page${titleSuffix(isTitle)}`,
  HistorySearch: (_, isTitle) => `Case History ${titleSuffix(isTitle)}`,
  Jobs: (_, isTitle) => `Jobs${titleSuffix(isTitle)}`,
  CaseDetails: (params, isTitle) => `Case View - ${params.casename as string}${titleSuffix(isTitle)}`,
  CaseAccessDetails: (params, isTitle) => `Case Access View - ${params.casename as string}${titleSuffix(isTitle)}`,
  CaseDetailsUpload: (params, isTitle) => `Upload Records to Case ${params.casename as string}${titleSuffix(isTitle)}`,
  CaseDetailsQuarantine: (params, isTitle) => `Quarantine for Case ${params.casename as string}${titleSuffix(isTitle)}`,
  AuditSearch: (params, isTitle) => {
    const key = params.auditKeyId as string;
    return `${key ? `Audit Log View - ${key}` : "Audit Log Search Page"}${titleSuffix(isTitle)}`;
  },
} satisfies Record<string, RouteTitles>;

// Keyed by case, so moving straight from one case to another (e.g. the agent's set_case) remounts
// the case page and loads the new case, as arriving from the brand page does.
const CaseRouterView = defineComponent({
  setup() {
    const route = useRoute();
    return () => h(RouterView, { key: route.params.casename as string });
  },
});

export const routes: RouterOptions["routes"] = [
  // Special EDX functionality
  { path: "/logs", name: "logs", component: RsLogs },
  { path: "/auth", name: "auth", component: RsAuth },
  {
    path: "/",
    name: "root",
    component: RsRoot,
    meta: { pageTitle: titles.default, showHeading: false },
    children: [
      { path: ":brand/error/:code", name: "error", component: RsError, props: true },
      {
        path: ":brand",
        component: RsBrandRoot,
        children: [
          // Error routes
          {
            path: "error/:code",
            name: "brandError",
            component: RsError,
            props: true,
          },
          // Main page routes
          {
            path: "",
            component: RsBrand,
            children: [
              {
                name: "brand",
                path: "",
                component: CaseSearch,
                meta: { pageTitle: titles.CaseSearch, showHeading: true },
                children: [
                  {
                    path: "access/:casename",
                    name: "caseAccess",
                    component: CaseAccess,
                    meta: { pageTitle: titles.CaseAccessDetails, showHeading: false },
                  },
                ],
              },
              {
                path: "audit/:auditKeyId?",
                name: "brandaudit",
                component: RsAudit,
                meta: { pageTitle: titles.AuditSearch, showHeading: true },
              },
              {
                path: "history",
                name: "brandhistory",
                component: CaseHistory,
                meta: { pageTitle: titles.HistorySearch, showHeading: true },
              },
              { name: "jobs", path: "jobs", component: Jobs, meta: { pageTitle: titles.Jobs, showHeading: true } },
            ],
          },
          {
            path: ":casename",
            component: CaseRouterView,
            meta: { pageTitle: titles.CaseDetails, showHeading: true, isCaseReference: true },
            children: [
              {
                path: "",
                name: "case",
                component: RsCase,
                children: [
                  {
                    path: "upload",
                    name: "upload",
                    component: RsUpload,
                    meta: { pageTitle: titles.CaseDetailsUpload, showHeading: true },
                  },
                  {
                    path: "quarantine",
                    name: "quarantine",
                    component: RsQuarantine,
                    meta: { pageTitle: titles.CaseDetailsQuarantine, showHeading: true },
                  },
                  {
                    path: ":casefile",
                    name: "casefile",
                    component: RouterView,
                    props: true,
                    children: [
                      {
                        path: "annotation-:annotation",
                        name: "casefileannotation",
                        component: RouterView,
                        props: true,
                      },
                      { path: "page-:filepage", name: "casefilepage", component: RouterView, props: true },
                      {
                        path: "ai-annotation-:annotation",
                        name: "casefileaiannotation",
                        component: RouterView,
                        props: true,
                      },
                      { path: "snippet-:snippet", name: "casefilesnippet", component: RouterView, props: true },
                      { path: "time-:time", name: "casefiletime", component: RouterView, props: true },
                      {
                        path: "time-range-:startTime-:endTime",
                        name: "casefiletimerange",
                        component: RouterView,
                        props: true,
                      },
                      { path: "field-:fieldPath", name: "casefilefieldbookmark", component: RouterView, props: true },
                    ],
                  },
                ],
              },
              {
                path: ":casefile/audit/:auditKeyId?",
                name: "fileaudit",
                component: RsAudit,
                meta: { pageTitle: titles.AuditSearch, showHeading: true },
              },
              {
                path: "audit/:auditKeyId?",
                name: "caseaudit",
                component: RsAudit,
                meta: { pageTitle: titles.AuditSearch, showHeading: true },
              },
              { path: "evidence/:category", name: "evidence", component: RsEvidenceFrame, props: true },
            ],
          },
          {
            path: "/:brand/activities",
            name: "activities",
            component: RsActivities,
            props: true,
          },
          {
            path: "/:brand/summarizer",
            redirect: (to) => ({ name: "summarizer-moved", params: { brand: to.params.brand } }),
          },
          {
            path: "/:brand/summarizer/test",
            redirect: (to) => ({ name: "summarizer-moved", params: { brand: to.params.brand } }),
          },
          {
            path: "/:brand/summarizer-moved",
            name: "summarizer-moved",
            component: SummarizerMoved,
            meta: { pageTitle: "Summarizer configuration has moved", showHeading: true },
          },
          /**
              {
                  path: '/:brand/:casename/snapshot/:casefile/:automated?', name: 'snapshot',
                  component: require('./pages/snapshot/snapshot.vue').default, props: true,
              },
              {
                  path: '/:brand/:casename/snipper/:casefile/:automated?', name: 'snipper',
                  component: require('./pages/snipper/snipper.vue').default, props: true,
              },
              {
                  path: '/:brand/:casename/snipperFrame/:tag/:maxHeight?', name: 'snipperFrame',
                  component: require('./pages/snipperframe/snipperframe.vue').default, props: true,
              },
              {
                  path: '/:brand/:casename/frameTester/:type/:tag/:maxHeight?', name: 'frametester',
                  component: require('./pages/frametester/frametester.vue').default, props: true,
              },
              {
                  path: '/:brand/:casename/redactor/:casefile/:automated?', name: 'redactor',
                  component: require('./pages/redactor/redactor.vue').default, props: true,
              },
              // EDX Routes

            */
        ],
      },
    ],
  },
  { path: "/external-login", component: RouterView, beforeEnter: () => window.close() },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.afterEach((to) => {
  let pageTitle = (to.meta?.pageTitle ?? titles.default) as RouteTitles;
  if (typeof pageTitle === "function") pageTitle = pageTitle(to.params, true);
  document.title = pageTitle;
});

export default router;
